import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { paragon } from '@useparagon/connect';
import { ENV, REGION } from 'constants/Global.constants';

import type { ClientConnection, IntegrationProvider } from '@polyai/schemas';
import {
  selectAllIntegrations,
  selectConnectionInEnvironment,
  selectConnectionsByProvider,
} from '@polyai/schemas';

import { useAppSelector } from 'hooks/reduxHooks';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { useParagonSecrets } from 'hooks/useParagonSecrets';
import { useParagonAuth } from 'providers/ParagonProvider';
import { resolveParagonProviderId } from 'screens/Integrations/constants';

const EU_REGIONS = ['uk-1', 'euw-1'];
export const isEuParagonRegion =
  EU_REGIONS.includes(REGION) && ENV === 'production';

export const paragonUrls = {
  host: isEuParagonRegion ? 'eu.paragon.so' : undefined,
  proxyBaseUrl: isEuParagonRegion
    ? 'https://worker-proxy.eu.paragon.so'
    : 'https://proxy.useparagon.com',
  oauthBaseUrl: isEuParagonRegion
    ? 'https://passport.eu.paragon.so'
    : 'https://passport.useparagon.com',
} as const;

export const useParagonUrls = () => paragonUrls;

// Paragon SDK authentication error names
const PARAGON_AUTH_ERROR_NAMES = [
  'UserNotAuthenticatedError',
  'HeadlessModeNotEnabledError',
] as const;

/**
 * Type guard to check if an error is a Paragon authentication error
 */
const isParagonAuthError = (error: unknown): boolean => {
  if (error instanceof Error) {
    // Check for specific Paragon auth error names
    if (
      PARAGON_AUTH_ERROR_NAMES.includes(
        error.name as (typeof PARAGON_AUTH_ERROR_NAMES)[number],
      )
    ) {
      return true;
    }

    // Check for token expiration in error message
    const errorMessage = error.message?.toLowerCase() || '';
    if (
      errorMessage.includes('token has expired') ||
      errorMessage.includes('token expired')
    ) {
      return true;
    }
  }

  // Check for 401 responses with token expiration
  if (error && typeof error === 'object') {
    const errorObj = error as any;

    // Check status code
    if (errorObj.status === 401 || errorObj.statusCode === 401) {
      return true;
    }

    // Check error code from Paragon (e.g., "7203")
    if (errorObj.code === '7203' || errorObj.code === 7203) {
      return true;
    }

    // Check message in nested structure
    if (
      errorObj.message
        ?.toLowerCase()
        .includes('authorization token has expired')
    ) {
      return true;
    }
  }

  return false;
};

export const useParagonOperation = () => {
  const { reauthenticate } = useParagonAuth();
  const allIntegrations = useAppSelector(state =>
    selectAllIntegrations(state.integrations),
  );

  const executeWithRetry = useCallback(
    async <T>(
      operation: () => Promise<T>,
      operationName = 'Paragon operation',
    ): Promise<T> => {
      try {
        // First attempt
        return await operation();
      } catch (error: unknown) {
        // Check if it's an authentication error using error.name
        if (isParagonAuthError(error)) {
          console.log(
            `${operationName} failed with auth error, attempting re-authentication...`,
          );

          const reAuthSuccess = await reauthenticate();

          if (reAuthSuccess) {
            try {
              return await operation();
            } catch (retryError) {
              console.error(
                `${operationName} failed after re-authentication:`,
                retryError,
              );
              throw retryError;
            }
          }
        }

        throw error;
      }
    },
    [reauthenticate],
  );

  const ensureAuthenticated = useCallback(async (): Promise<boolean> => {
    try {
      const user = paragon.getUser();

      if (!user?.authenticated) {
        return await reauthenticate();
      }

      return true;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return await reauthenticate();
    }
  }, [reauthenticate]);

  const disconnectIntegration = useCallback(
    async (paragonProviderId: string, credentialsId: string) => {
      await paragon.uninstallIntegration(paragonProviderId, {
        selectedCredentialId: credentialsId,
      });
    },
    [],
  );

  const disconnectAllIntegrations = useCallback(async () => {
    const uninstalls = allIntegrations.flatMap(config =>
      Object.keys(config.connections).map(connectionId =>
        paragon
          .uninstallIntegration(config.provider, {
            selectedCredentialId: connectionId,
          })
          .catch(err => {
            console.error(
              `Failed to uninstall ${config.provider} (${connectionId}):`,
              err,
            );
          }),
      ),
    );
    await Promise.allSettled(uninstalls);
  }, [allIntegrations]);

  return {
    executeWithRetry,
    ensureAuthenticated,
    reauthenticate,
    disconnectIntegration,
    disconnectAllIntegrations,
  };
};

export function useParagonIntegrationMetadata() {
  const { executeWithRetry } = useParagonOperation();

  return useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      return await executeWithRetry(
        async () => paragon.getIntegrationMetadata(),
        'Get integration metadata',
      );
    },
  });
}

export function useParagonIntegrationConfig(type: string) {
  const { executeWithRetry } = useParagonOperation();
  const resolvedType = resolveParagonProviderId(type);

  return useQuery({
    queryKey: ['integrationConfig', resolvedType],
    queryFn: async () => {
      return await executeWithRetry(
        async () => paragon.getIntegrationConfig(resolvedType),
        'Get integration config',
      );
    },
  });
}

export function useParagonAuthenticatedUser() {
  const { executeWithRetry } = useParagonOperation();

  return useQuery({
    queryKey: ['user'],
    queryFn: () => {
      return executeWithRetry(async () => {
        const user = paragon.getUser();
        if (!user.authenticated) {
          throw new Error('User is not authenticated');
        }
        return user;
      }, 'Get authenticated user');
    },
  });
}

type FieldOptionsResponse = Awaited<ReturnType<typeof paragon.getFieldOptions>>;

const fieldOptionsInitialData: FieldOptionsResponse = {
  data: [],
  nestedData: [],
  nextPageCursor: null,
};

export function useParagonFieldOptions({
  integration,
  sourceType,
  search,
  cursor,
  parameters = [],
  enabled = true,
}: {
  integration: string;
  sourceType: string;
  search?: string;
  cursor?: string | number | false;
  parameters?: { cacheKey: string; value: string | undefined }[];
  enabled?: boolean;
}) {
  const { executeWithRetry } = useParagonOperation();
  const resolvedIntegration = resolveParagonProviderId(integration);

  return useQuery({
    enabled: enabled,
    queryKey: [
      'fieldOptions',
      resolvedIntegration,
      sourceType,
      search,
      cursor,
      parameters,
    ],
    queryFn: async () => {
      if (sourceType) {
        return await executeWithRetry(
          () =>
            paragon.getFieldOptions({
              integration: resolvedIntegration,
              action: sourceType,
              search,
              cursor,
              parameters: parameters.map(parameter => {
                return {
                  key: parameter.cacheKey,
                  source: {
                    type: 'VALUE',
                    value: parameter.value,
                  },
                };
              }),
            }),
          'Get field options',
        );
      }
      return fieldOptionsInitialData;
    },
    initialData: fieldOptionsInitialData,
  });
}

export function useParagonDataSourceOptions<T>(
  integration: string,
  sourceType: string,
) {
  const { executeWithRetry } = useParagonOperation();
  const resolvedIntegration = resolveParagonProviderId(integration);

  return useQuery({
    queryKey: ['comboInputOptions', resolvedIntegration, sourceType],
    queryFn: async () => {
      return await executeWithRetry(
        () =>
          paragon.getDataSourceOptions(
            resolvedIntegration,
            sourceType,
          ) as Promise<T>,
        'Get data source options',
      );
    },
  });
}

/**
 * Hook to get all connections for a specific Paragon provider
 */
export const useParagonConnections = (
  provider: IntegrationProvider,
): ClientConnection[] => {
  const { isFeatureOn } = useIsFeatureOn();
  const useSecretsAPI = isFeatureOn('paragon_secrets');
  const { secret } = useParagonSecrets();

  // Get connections from Redux (legacy)
  const reduxConnections = useAppSelector(state =>
    state?.integrations
      ? selectConnectionsByProvider(state.integrations, provider)
      : [],
  );

  // Get connections from secrets API (new)
  const secretConnections = useMemo(() => {
    if (!useSecretsAPI || !secret) {
      return [];
    }

    // Find all connections for this provider
    return Object.values(secret.connections)
      .filter(connection => connection.provider === provider)
      .map(
        connection =>
          ({
            id: connection.paragonConnectionId,
            provider: connection.provider,
            paragonConnectionId: connection.paragonConnectionId,
            paragonUserId: connection.paragonUserId,
            metadata: connection.metadata,
            // Secrets API doesn't store creation metadata, using defaults
            createdAt: new Date().toISOString(),
            createdBy: 'system',
          }) as ClientConnection,
      );
  }, [useSecretsAPI, secret, provider]);

  return useSecretsAPI ? secretConnections : reduxConnections;
};

/**
 * Hook to check if a provider has any connections
 */
export const useHasParagonConnection = (
  provider: IntegrationProvider,
): boolean => {
  const connections = useParagonConnections(provider);
  return connections.length > 0;
};

/**
 * Hook to get connection status for each environment
 */
export const useParagonConnectionStatus = (provider: IntegrationProvider) => {
  const { isFeatureOn } = useIsFeatureOn();
  const useSecretsAPI = isFeatureOn('paragon_secrets');
  const { secret } = useParagonSecrets();
  const integrations = useAppSelector(state => state?.integrations);

  return useMemo(() => {
    // Use secrets API when feature flag is enabled
    if (useSecretsAPI && secret) {
      return {
        sandbox: (secret.sandbox[provider] ?? []).length > 0,
        preRelease: (secret.preRelease[provider] ?? []).length > 0,
        live: (secret.live[provider] ?? []).length > 0,
      };
    }

    // Fall back to Redux (legacy)
    if (!integrations) {
      return {
        sandbox: false,
        preRelease: false,
        live: false,
      };
    }

    return {
      sandbox: !!selectConnectionInEnvironment(
        integrations,
        provider,
        'sandbox',
      ),
      preRelease: !!selectConnectionInEnvironment(
        integrations,
        provider,
        'preRelease',
      ),
      live: !!selectConnectionInEnvironment(integrations, provider, 'live'),
    };
  }, [useSecretsAPI, secret, integrations, provider]);
};
